import { DateTime } from '@common/data-models/complex-types/date-time';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { UserStaffInfo } from '@common/data-models/service/user-staff-info';
import { ObjectTypes } from '@common/enums/object-types';
import { IGlobalTablesService } from '@common/interfaces/i-global-tables-service';
import { INomWebServicesJson } from '@common/interfaces/i-nom-web-services-json';
import { GlobalRoles, InvitationStatusCodes } from '@common/utils/util';
import { ViewStateOrder } from '@src/suite/common/data-models/complex-types/view-state-order';
import { LayoutState } from '@suite/common/services/layout-state';
import moment from 'moment-timezone';

import { GroupKey } from './group-key';

export class ResultGroupStaticServices {
    public static globalTablesService: IGlobalTablesService;
    public static nomWebServicesJson: INomWebServicesJson;
    public static viewStateService: LayoutState;
}

export type GroupValueConvertor = (rec: StorytellingRecord) => Array<GroupKey>;
export type GroupValueConvertorObject<T> = (rec: T) => Array<GroupKey>;
export class GroupValueHelpers {
    public static MultiValueOrDefault(pFieldName: string, pDefaultValue: string): GroupValueConvertor {
        const defaultValue = pDefaultValue;
        const fieldName = pFieldName;
        return (rec: StorytellingRecord) => {
            const groupVal = rec?.[fieldName] || new MultiValue<string>();
            if (groupVal) {
                if (groupVal.length === 0) {
                    return [{ display: defaultValue, value: null }];
                }
                const ret = new Array<{ display: string; value: string }>();
                for (const key of groupVal) {
                    try {
                        if (!Object.keys(ResultGroupStaticServices.globalTablesService.idToDescriptionMap).length) {
                            ResultGroupStaticServices.globalTablesService.getListItemsForDesk(rec.Type);
                        }
                    } catch (e) {
                        console.log(e);
                    }

                    let description = ResultGroupStaticServices.globalTablesService.idToDescriptionMap[key];

                    if (!description) {
                        description = key;
                    }
                    ret.push({ display: description, value: key });
                }
                return ret;
            }
            return [{ display: defaultValue, value: null }];
        };
    }

    public static MultiDay(pFieldName: string): GroupValueConvertor {
        const fieldName = pFieldName;

        return (rec: StorytellingRecord) => {
            const groupVal = rec?.[fieldName];
            if (groupVal.from || groupVal.to) {
                const itemRangeStart = groupVal.from;
                const itemRangeEnd = groupVal.to;
                const currentDate = new Date();
                const rangeEndFormat = moment(itemRangeEnd.toUTCString());
                const rangeStartFormat = moment(itemRangeStart.toUTCString());
                const diff = rangeEndFormat.diff(rangeStartFormat, 'days') + 1;
                if (groupVal.get()) {
                    try {
                        const ret = new Array<{ display: string; value: string }>();
                        const rangeStart = new Date(
                            moment(currentDate).startOf('isoWeek').add(ResultGroupStaticServices.nomWebServicesJson.HelloResults.DayOfWeek, 'days').subtract(1, 'day').toDate()
                        );
                        const rangeEnd = new Date(moment(currentDate).endOf('isoWeek').subtract(1, 'second').toDate());

                        for (let j = 0; j < diff; j++) {
                            // Intl.DateTimeFormat() throws error only when "(UTC +01:00) Sao Tome" is set.
                            const formatter = new Intl.DateTimeFormat('en-us', { month: 'long' });

                            const topicDate = moment.tz(itemRangeStart.toUTCString()).add(j, 'days').toDate();

                            // if (
                            //     ResultGroupStaticServices.globalTablesService.PersonalTimeZone &&
                            //     ResultGroupStaticServices.globalTablesService.ClientTimeZone &&
                            //     ResultGroupStaticServices.globalTablesService.PersonalTimeZone.id !== ResultGroupStaticServices.globalTablesService.ClientTimeZone.id
                            // ) {
                            //     topicDate = DateTime.convertToTargetTZ(
                            //         topicDate,
                            //         ResultGroupStaticServices.globalTablesService.PersonalTimeZone.id,
                            //         ResultGroupStaticServices.globalTablesService.ClientTimeZone.id
                            //     );
                            // }
                            const groupValDisplay = `${formatter.format(topicDate).toString()} ${topicDate.getDate().toString()}, ${topicDate.getFullYear().toString()}`;
                            const v = DateTime.handleDate(topicDate);
                            // @TODO KAREV
                            /*if (topicDate >= rangeStart && (<TopicViewStateRecord>ResultGroupStaticServices.viewStateService.ViewStateRecord).viewStateLayout.Value === ViewStateLayouts.CARD) {
                                ret.push({ display: groupValDisplay, value: v });
                            } else if (topicDate >= rangeStart && topicDate <= rangeEnd) {
                                ret.push({ display: groupValDisplay, value: v });
                            }*/
                            if (topicDate >= rangeStart && topicDate <= rangeEnd) {
                                ret.push({ display: groupValDisplay, value: v });
                            }
                        }
                        return ret;
                    } catch (err) {
                        return [{ display: 'No Value', value: '' }];
                    }
                } else {
                    return [{ display: 'No Value', value: '' }];
                }
            }
        };
    }

    public static LookupListOrDefault(pFieldName: string, pDefaultValue: string): GroupValueConvertor {
        const defaultValue = pDefaultValue;
        const fieldName = pFieldName;
        return (rec: StorytellingRecord) => {
            const groupVal = rec?.[fieldName];
            if (groupVal) {
                if (groupVal.keys.length === 0) {
                    return [{ display: defaultValue, value: null }];
                }
                const ret = new Array<{ display: string; value: string }>();
                for (const item of groupVal) {
                    let description = item.displayText;
                    if (!description) {
                        description = item.id;
                    }
                    ret.push({ display: description, value: item.id });
                }
                return ret;
            }
            return [{ display: defaultValue, value: null }];
        };
    }

    public static StringOrDefault(pFieldName: string, pDefaultValue: string): GroupValueConvertor {
        const defaultValue = pDefaultValue;
        const fieldName = pFieldName;
        return (rec: StorytellingRecord) => {
            let key = rec?.[fieldName];
            const { Type } = rec;
            if (!key) key = defaultValue;
            if (fieldName === PSTFieldNames.PSTWirePriority && Type === ObjectTypes.WIRE) {
                key = `WIRE_PRIORITY_${key}`;
            }
            if (fieldName === PSTFieldNames.PSTWireType && Type === ObjectTypes.DISCOVER_WIRE) {
                key = `WIRE_TYPE_${key}`;
            }

            let description = ResultGroupStaticServices.globalTablesService.idToDescriptionMap[key];
            if (!description) {
                description = key;
            }
            return [{ display: description, value: key }];
        };
    }

    public static Boolean(pFieldName: string): GroupValueConvertor {
        const fieldName = pFieldName;
        return (rec: StorytellingRecord) => {
            const groupVal = rec[fieldName];
            if (groupVal === true) return [{ display: ResultGroupStaticServices.globalTablesService.languageSettings.PSTWebYesOption, value: '1' }];
            if (groupVal === false) return [{ display: ResultGroupStaticServices.globalTablesService.languageSettings.PSTWebNoOption, value: '0' }];
            return [{ display: 'No Value', value: '' }];
        };
    }

    public static SingleGroup(): GroupValueConvertor {
        return () => [{ display: 'Single Group', value: 'SingleGroup' }];
    }

    public static DateTime(pFieldName: string): GroupValueConvertor {
        const fieldName = pFieldName;
        return (rec: StorytellingRecord) => {
            let groupVal = rec[fieldName];
            if (!groupVal) groupVal = new DateTime();
            if (groupVal.get() && !(groupVal.get().getUTCFullYear() === 1800)) {
                try {
                    // Intl.DateTimeFormat() throws error only when "(UTC +01:00) Sao Tome" is set.
                    const formatter = new Intl.DateTimeFormat('en-us', { month: 'long' });

                    let dateVal = new Date(groupVal.moment.toDate());

                    if (
                        ResultGroupStaticServices.globalTablesService.PersonalTimeZone &&
                        ResultGroupStaticServices.globalTablesService.ClientTimeZone &&
                        ResultGroupStaticServices.globalTablesService.PersonalTimeZone.id !== ResultGroupStaticServices.globalTablesService.ClientTimeZone.id
                    ) {
                        dateVal = DateTime.convertToTargetTZ(
                            dateVal,
                            ResultGroupStaticServices.globalTablesService.PersonalTimeZone.id,
                            ResultGroupStaticServices.globalTablesService.ClientTimeZone.id
                        );
                    }
                    const groupValDisplay = `${formatter.format(dateVal).toString()} ${dateVal.getDate().toString()}, ${dateVal.getFullYear().toString()}`;
                    const v = DateTime.handleDate(dateVal);
                    return [{ display: groupValDisplay, value: v }];
                } catch (err) {
                    return [{ display: 'No Value', value: '' }];
                }
            } else {
                return [{ display: 'No Value', value: '' }];
            }
        };
    }

    public static Time(pFieldName: string): GroupValueConvertor {
        const fieldName = pFieldName;
        return (rec: StorytellingRecord) => {
            let groupVal = rec?.[fieldName];
            if (!groupVal) groupVal = new DateTime();
            if (groupVal.get() && !(groupVal.get().getUTCFullYear() === 1800)) {
                try {
                    const groupValDisplay = groupVal.moment.format('hA, MMM DD, YYYY');
                    const dateVal = new Date(groupVal.get().getFullYear(), groupVal.get().getMonth(), groupVal.get().getDate(), groupVal.get().getHours());
                    return [{ display: groupValDisplay, value: dateVal.toISOString() }];
                } catch (err) {
                    return [{ display: 'No Value', value: '' }];
                }
            } else {
                return [{ display: 'No Value', value: '' }];
            }
        };
    }

    public static StaffOrDefault(pFieldName: string, pDefaultValue: string): GroupValueConvertor {
        const defaultValue = pDefaultValue;
        const fieldName = pFieldName;
        return (rec: StorytellingRecord) => {
            const val: MultiValue<string> = rec?.[fieldName] || new MultiValue<string>();
            const staffIds: Array<GroupKey> = [];
            if (val && val.length > 0) {
                for (const key of val) {
                    const staff = ResultGroupStaticServices.globalTablesService.Staff[key];
                    const groupVal = staff ? `${staff.GivenName} ${staff.Surname}` : defaultValue;
                    staffIds.push(new GroupKey(staff.UserID, groupVal));
                }
            } else {
                staffIds.push(new GroupKey(defaultValue, defaultValue));
            }
            return staffIds;
        };
    }

    public static RoleOrDefault(pType: string, pDefaultValue: string): GroupValueConvertor {
        const defaultValue = pDefaultValue;
        const type = pType;
        return (rec: StorytellingRecord) => {
            const roles: Array<GroupKey> = [];
            for (const role of ResultGroupStaticServices.globalTablesService.getRolePropertiesFor(type)) {
                const recProp: MultiValue<string> = rec?.[role.ID] || new MultiValue<string>();
                if (
                    recProp &&
                    (recProp.hasKey(ResultGroupStaticServices.nomWebServicesJson.HelloResults.UserID) ||
                        recProp.hasKey(`${role.ID}.${ResultGroupStaticServices.nomWebServicesJson.HelloResults.UserID}`))
                ) {
                    roles.push(new GroupKey(role.ID, role.DefaultCaption));
                }
            }
            if (roles.length === 0) roles.push(new GroupKey(defaultValue, defaultValue));
            return roles;
        };
    }
}

export type GroupKeyComparator = (a: GroupKey, b: GroupKey) => number;
export type GroupKeyComparatorFunc = (sortOrder: string) => GroupKeyComparator;

export class GroupKeyComparatorHelpers {
    public static DisplayValue(): GroupKeyComparatorFunc {
        return (sortOrder: string) => (a: GroupKey, b: GroupKey) => {
            if (a.display > b.display) return sortOrder === 'asc' ? 1 : -1;
            if (a.display < b.display) return sortOrder === 'asc' ? -1 : 1;
            return 0;
        };
    }

    public static Value(): GroupKeyComparatorFunc {
        return (sortOrder: string) => (a: GroupKey, b: GroupKey) => {
            if (a.value > b.value) return sortOrder === 'asc' ? 1 : -1;
            if (a.value < b.value) return sortOrder === 'asc' ? -1 : 1;
            return 0;
        };
    }

    public static SingleGroup(): GroupKeyComparatorFunc {
        return () => () => 0;
    }

    public static ValueDesc(): GroupKeyComparator {
        return (a: GroupKey, b: GroupKey) => {
            if (a.value > b.value) return -1;
            if (a.value < b.value) return 1;
            return 0;
        };
    }

    public static DateValue(): GroupKeyComparatorFunc {
        return (sortOrder: string) => (a: GroupKey, b: GroupKey) => {
            const aValue: Date = new Date(a.value);
            const bValue: Date = new Date(b.value);

            if (b.value && !a.value) {
                return sortOrder === 'asc' ? 1 : -1;
            }
            if (!b.value && a.value) {
                return sortOrder === 'asc' ? -1 : 1;
            }
            if (!b.value && !a.value) {
                return 0;
            }
            return sortOrder === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
        };
    }

    private static NaturalSort(s: string): string {
        return ` ${s} `
            .replace(/[\s]+/g, ' ')
            .toLowerCase()
            .replace(/[\d]+/, d => {
                d = `${1e20}${d}`;
                return d.substring(d.length - 20);
            });
    }

    public static DisplayValueDefaultBottom(pDefaultValue: string): GroupKeyComparatorFunc {
        return (sortOrder: string) => {
            const defaultValue = pDefaultValue;
            return (a: GroupKey, b: GroupKey) => {
                if (a.display === defaultValue && b.display === defaultValue) {
                    return 0;
                }
                if (a.display === defaultValue && b.display !== defaultValue) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                if (a.display !== defaultValue && b.display === defaultValue) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                const aValue = GroupKeyComparatorHelpers.NaturalSort(a.display);
                const bValue = GroupKeyComparatorHelpers.NaturalSort(b.display);

                const oRes = aValue.localeCompare(bValue);
                if (oRes > 0) return sortOrder === 'asc' ? 1 : -1;
                if (oRes < 0) return sortOrder === 'asc' ? -1 : 1;
                return 0;
            };
        };
    }
}

export type RecordComparator = (a: StorytellingRecord, b: StorytellingRecord) => number;
export type ObjectComparator<T> = (a: T, b: T) => number;
export type RecordComparatorFunc = (sortOrder: string) => RecordComparator;
export type ObjectComparatorFunc<T> = (sortOrder: string) => ObjectComparator<T>;

export class RecordComparatorHelpers {
    private static NaturalSort(s: string): string {
        return ` ${s} `
            .replace(/[\s]+/g, ' ')
            .toLowerCase()
            .replace(/[\d]+/, d => {
                d = `${1e20}${d}`;
                return d.substring(d.length - 20);
            });
    }

    private static GroupByParent(a: StorytellingRecord, b: StorytellingRecord, sortOrder: string): number {
        if (!!a.ParentGuid && !!b.ParentGuid) {
            const aValue: string = a.ParentGuid;
            const bValue: string = b.ParentGuid;

            const oRes = aValue.localeCompare(bValue);
            if (oRes > 0) return sortOrder === 'asc' ? 1 : -1;
            if (oRes < 0) return sortOrder === 'asc' ? -1 : 1;
            return 0;
        }
        return 0;
    }

    public static Generic(pFieldName: string): RecordComparatorFunc {
        return (sortOrder: string) => {
            const fieldName = pFieldName;
            return (a: StorytellingRecord, b: StorytellingRecord) => {
                let aValue: string = a[fieldName];
                let bValue: string = b[fieldName];

                aValue = RecordComparatorHelpers.NaturalSort(aValue);
                bValue = RecordComparatorHelpers.NaturalSort(bValue);

                const oRes = aValue.localeCompare(bValue);
                if (oRes > 0) return sortOrder === 'asc' ? 1 : -1;
                if (oRes < 0) return sortOrder === 'asc' ? -1 : 1;
                return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
            };
        };
    }

    private static boolComparable(val: boolean): number {
        let valNorm = null;
        if (val === true) valNorm = 3;
        else if (val === false) valNorm = 2;
        else valNorm = 1;
        return valNorm;
    }

    public static Boolean(pFieldName: string): RecordComparatorFunc {
        return (sortOrder: string) => {
            const fieldName = pFieldName;
            return (a: StorytellingRecord, b: StorytellingRecord) => {
                const aValue = RecordComparatorHelpers.boolComparable(a[fieldName]);
                const bValue = RecordComparatorHelpers.boolComparable(b[fieldName]);
                if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
                if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
                return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
            };
        };
    }

    public static VSGuid: string = null;

    public static Manual(): RecordComparatorFunc {
        return (sortOrder: string) => (a: StorytellingRecord, b: StorytellingRecord) => {
            let aValue: number = 0;
            const oATopicOrder = <ViewStateOrder>a[PSTFieldNames.PSTTopicOrder] || new ViewStateOrder();
            if (oATopicOrder.hasValues()) {
                aValue = oATopicOrder.get(RecordComparatorHelpers.VSGuid);
            }
            if (!aValue) {
                const dateValue = a['CreatedDateTime'];
                if (dateValue) {
                    aValue = moment(dateValue.get()).unix() * 1000;
                }
            }

            let bValue: number = 0;
            const oBTopicOrder = <ViewStateOrder>b[PSTFieldNames.PSTTopicOrder] || new ViewStateOrder();
            if (oBTopicOrder.hasValues()) {
                bValue = oBTopicOrder.get(RecordComparatorHelpers.VSGuid);
            }
            if (!bValue) {
                const dateValue = b['CreatedDateTime'];
                if (dateValue) {
                    bValue = moment(dateValue.get()).unix() * 1000;
                }
            }
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            return 0;
        };
    }

    public static String(pFieldName: string): RecordComparatorFunc {
        return (sortOrder: string) => {
            const fieldName = pFieldName;
            return (a: StorytellingRecord, b: StorytellingRecord) => {
                let aValue: string = a[fieldName];
                let bValue: string = b[fieldName];
                if (bValue && !aValue) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                if (!bValue && aValue) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (!bValue && !aValue) {
                    return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
                }
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
                if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
                if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
                return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
            };
        };
    }

    public static MultiValue(pFieldName: string): RecordComparatorFunc {
        return (sortOrder: string) => {
            const fieldName = pFieldName;
            return (a: StorytellingRecord, b: StorytellingRecord) => {
                let aValue = null;
                let bValue = null;
                const aVal = a[fieldName] || new MultiValue<string>();
                const bVal = b[fieldName] || new MultiValue<string>();
                aValue = aVal[0];
                bValue = bVal[0];
                if (bValue && !aValue) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                if (!bValue && aValue) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (!bValue && !aValue) {
                    return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
                }
                if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
                if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
                return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
            };
        };
    }

    public static LookupList(pFieldName: string): RecordComparatorFunc {
        return (sortOrder: string) => {
            const fieldName = pFieldName;
            return (a: StorytellingRecord, b: StorytellingRecord) => {
                let aValue = null;
                let bValue = null;
                const aVal = a[fieldName];
                const bVal = b[fieldName];
                if (aVal && aVal.keys && aVal.keys.length > 0) aValue = aVal.keys[0];
                if (bVal && bVal.keys && bVal.keys.length > 0) bValue = bVal.keys[0];
                if (bValue && !aValue) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                if (!bValue && aValue) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (!bValue && !aValue) {
                    return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
                }
                if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
                if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
                return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
            };
        };
    }

    public static DateTime(pFieldName: string): RecordComparatorFunc {
        return (sortOrder: string) => {
            const fieldName = pFieldName;
            return (a: StorytellingRecord, b: StorytellingRecord) => {
                const aField = a[fieldName] && a[fieldName].get ? a[fieldName].deepCopy().get() : null;
                const bField = b[fieldName] && b[fieldName].get ? b[fieldName].deepCopy().get() : null;
                if (bField && !aField) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                if (!bField && aField) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (!bField && !aField) {
                    return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
                }
                let timeDiff;
                if (sortOrder === 'asc') timeDiff = aField.getTime() - bField.getTime();
                else timeDiff = bField.getTime() - aField.getTime();
                if (timeDiff === 0) {
                    return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
                }
                return timeDiff;
            };
        };
    }

    public static StaffOrDefault(pFieldName: string, pDefaultValue: string): RecordComparatorFunc {
        return (sortOrder: string) => {
            const fieldName = pFieldName;
            const defaultValue = pDefaultValue;
            let aString: string = '';
            let bString: string = '';

            return (a: StorytellingRecord, b: StorytellingRecord) => {
                aString = '';
                let val = a[fieldName];
                if (val?.length) {
                    const aStaff = ResultGroupStaticServices.globalTablesService.Staff[val[0]];
                    aString = aStaff ? `${aStaff.GivenName} ${aStaff.Surname}` : defaultValue;
                }
                bString = '';
                val = b[fieldName];
                if (val?.length) {
                    const bStaff = ResultGroupStaticServices.globalTablesService.Staff[val[0]];
                    bString = bStaff ? `${bStaff.GivenName} ${bStaff.Surname}` : defaultValue;
                }
                if (aString > bString) return sortOrder === 'asc' ? 1 : -1;
                if (aString < bString) return sortOrder === 'asc' ? -1 : 1;
                return RecordComparatorHelpers.GroupByParent(a, b, sortOrder);
            };
        };
    }
}

// NORMAL OBJECT COMPARATORS
// TODO: This is remporary solution, need to be refactored
export class ObjectComparatorHelpers {
    public static DateTime<T>(pFieldName: string): ObjectComparatorFunc<T> {
        return (sortOrder: string) => {
            const fieldName = pFieldName;
            return (a: T, b: T) => {
                const aField = new Date(a[fieldName]);
                const bField = new Date(b[fieldName]);
                if (bField && !aField) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                if (!bField && aField) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                let timeDiff;
                if (sortOrder === 'asc') timeDiff = aField.getTime() - bField.getTime();
                else timeDiff = bField.getTime() - aField.getTime();
                return timeDiff;
            };
        };
    }
}

export class ResultGroupBase<T> {
    public entries: { [id: string]: T } = {};
    public groupDisplay: { [id: string]: string } = {};
    public payload: { [id: string]: any } = {};
    public keys: Array<GroupKey> = [];
    public isBuilt: boolean = false;
    public totalCost: { [id: string]: number } = {};

    public clear() {
        this.entries = {};
        this.groupDisplay = {};
        this.keys = [];
        this.isBuilt = false;
        this.payload = {};
    }
}

export class ResultGroup<T extends StorytellingRecord> extends ResultGroupBase<Array<T>> {
    public addToGroups(groupKeys: Array<GroupKey>, item: T) {
        if (groupKeys) {
            for (const groupKey of groupKeys) {
                this.groupDisplay[groupKey.value] = groupKey.display;
                this.payload[groupKey.value] = groupKey.payload;

                if (!this.entries[groupKey.value]) {
                    this.entries[groupKey.value] = [];
                    this.keys.push(groupKey);
                }
                if (!this.totalCost[groupKey.value]) {
                    this.totalCost[groupKey.value] = 0;
                }

                this.entries[groupKey.value].push(item);
                this.totalCost[groupKey.value] += item?.totalCost || 0;
            }
        }
    }

    public insertAfterEntry(groupKeys: Array<GroupKey>, item: T, itemToInsert: T) {
        for (const groupKey of groupKeys) {
            if (this.entries[groupKey.value]) {
                const index = this.entries[groupKey.value].indexOf(item);
                this.entries[groupKey.value].splice(index + 1, 0, itemToInsert);
            }
            this.groupDisplay[groupKey.value] = groupKey.display;
            this.payload[groupKey.value] = groupKey.payload;
        }
    }

    public remove(groupKeys: Array<GroupKey>, item: T) {
        const emptyGroupsToRemove: Array<string> = [];

        for (const key in this.entries) {
            const index = this.entries[key].indexOf(item);
            if (index >= 0) this.entries[key].splice(index, 1);
            if (this.entries[key].length === 0) emptyGroupsToRemove.push(key);
        }

        for (const emptyGroup of emptyGroupsToRemove) {
            delete this.entries[emptyGroup];
            delete this.groupDisplay[emptyGroup];
            let index = -1;
            for (let i = 0; i < this.keys.length; i++) {
                if (this.keys[i].value === emptyGroup) {
                    index = i;
                    break;
                }
            }
            if (index >= 0) this.keys.splice(index, 1);
        }
    }

    public sort(keyComparator: GroupKeyComparator, itemComparator: RecordComparator) {
        this.keys = [];
        for (const key of Object.keys(this.entries)) {
            this.keys.push(new GroupKey(key, this.groupDisplay[key], this.payload[key]));
        }
        this.keys.sort(keyComparator);
        for (const key of this.keys) {
            this.entries[key.value].sort(itemComparator);
        }
    }
}

export class ResultGroupingDefinition {
    constructor(
        public valueConvertor?: GroupValueConvertor,
        public groupKeyComparator?: GroupKeyComparatorFunc,
        public recordComparator?: RecordComparatorFunc
    ) {}
}

export class ResultGroup2<T extends StorytellingRecord> extends ResultGroupBase<ResultGroup<T>> {
    public addToGroups(groupKeys: Array<GroupKey>, groupKeys2: Array<GroupKey>, item: T) {
        for (const groupKey of groupKeys) {
            this.groupDisplay[groupKey.value] = groupKey.display;
            this.payload[groupKey.value] = groupKey.payload;
            if (!this.entries[groupKey.value]) {
                this.entries[groupKey.value] = new ResultGroup<T>();
                this.keys.push(groupKey);
            }
            this.entries[groupKey.value].addToGroups(groupKeys2, item);
        }
    }

    public insertAfterEntry(groupKeys: Array<GroupKey>, insertAfter: T, itemToInsert: T) {
        for (const groupKey of groupKeys) {
            const objGroupKey = this.entries[groupKey.value];
            if (objGroupKey) {
                const innerGroups = this.entries[groupKey.value];
                for (const innerGroupKey of innerGroups.keys) {
                    const index = innerGroups.entries[innerGroupKey.value].indexOf(insertAfter);
                    if (index > -1) {
                        innerGroups.entries[innerGroupKey.value].splice(index + 1, 0, itemToInsert);
                    }
                }
            }
            this.groupDisplay[groupKey.value] = groupKey.display;
            this.payload[groupKey.value] = groupKey.payload;
        }
    }

    public remove(groupKeys: Array<GroupKey>, item: T) {
        const emptyGroupsToRemove: Array<string> = [];

        for (const key in this.entries) {
            this.entries[key].remove(groupKeys, item);
            if (Object.keys(this.entries[key].entries).length === 0) {
                emptyGroupsToRemove.push(key);
            }
        }

        for (const emptyGroup of emptyGroupsToRemove) {
            delete this.entries[emptyGroup];
            delete this.groupDisplay[emptyGroup];
            let index = -1;
            for (let i = 0; i < this.keys.length; i++) {
                if (this.keys[i].value === emptyGroup) {
                    index = i;
                    break;
                }
            }
            if (index >= 0) this.keys.splice(index, 1);
        }
    }

    public sort(keyComparator: GroupKeyComparator, keyComparator2: GroupKeyComparator, itemComparator: RecordComparator) {
        this.keys = [];
        for (const key of Object.keys(this.entries)) {
            this.keys.push(new GroupKey(key, this.groupDisplay[key], this.payload[key]));
        }
        this.keys.sort(keyComparator);
        for (const key of this.keys) {
            this.entries[key.value].sort(keyComparator2, itemComparator);
        }
    }

    public clear() {
        for (const key in this.entries) {
            this.entries[key].clear();
        }
        super.clear();
    }
}

export class ResultGrouping2Definition {
    constructor(
        public valueConvertor?: GroupValueConvertor,
        public groupKeyComparator?: GroupKeyComparatorFunc,
        public valueConvertor2?: GroupValueConvertor,
        public groupKeyComparator2?: GroupKeyComparatorFunc,
        public recordComparator?: RecordComparatorFunc
    ) {}
}

export type GroupUserValueConvertor = (rec: UserStaffInfo) => Array<GroupKey>;
export class GroupUserValueHelpers {
    public static StringOrDefault(pFieldName: string, pDefaultValue: string): GroupUserValueConvertor {
        const defaultValue = pDefaultValue;
        const fieldName = pFieldName;
        return (rec: UserStaffInfo) => {
            let groupVal = rec.getAs<string>(fieldName);
            if (!groupVal) groupVal = defaultValue;
            return [{ display: groupVal, value: groupVal }];
        };
    }

    public static UserInfoStatusStringOrDefault(pFieldName: string, pDefaultValue: string): GroupUserValueConvertor {
        const defaultValue = pDefaultValue;
        const fieldName = pFieldName;
        return (rec: UserStaffInfo) => {
            let status = rec.getAs<string>(fieldName);
            if (!status) status = defaultValue;
            let groupVal = '';
            let groupLabel = '';

            if (status === InvitationStatusCodes.ACTIVE) {
                groupLabel = ResultGroupStaticServices.globalTablesService.languageSettings.PSTWebAllTeamMembersLabel;
            } else if (status === InvitationStatusCodes.PENDING) {
                groupLabel = ResultGroupStaticServices.globalTablesService.languageSettings.PSTWebPendingTeamMembersLabel;
            } else {
                groupLabel = ResultGroupStaticServices.globalTablesService.languageSettings.PSTWebNewTeamMembersLabel;
            }
            groupVal = status;

            return [{ display: groupLabel, value: groupVal }];
        };
    }
}
export class UserGroup<T extends UserStaffInfo> extends ResultGroupBase<Array<T>> {
    public static USERS_PER_PAGE: number = 20;
    public currentPageForGroup: { [id: string]: number } = {};

    public getCurrentPage(group: string): number {
        if (!this.currentPageForGroup[group]) this.currentPageForGroup[group] = 0;
        return this.currentPageForGroup[group];
    }

    public setCurrentPage(group: string, page: number) {
        this.currentPageForGroup[group] = page;
    }

    public getPagesCount(group: string): number {
        let ret = Math.floor(this.entries[group].length / UserGroup.USERS_PER_PAGE);
        if (this.entries[group].length % UserGroup.USERS_PER_PAGE) ret++;
        return ret;
    }

    public getPages(group: string): Array<number> {
        return [...Array(this.getPagesCount(group)).keys()].map(i => ++i);
    }

    public getItemsForCurrentPage(group: string): Array<T> {
        return this.entries[group].slice(UserGroup.USERS_PER_PAGE * this.getCurrentPage(group), UserGroup.USERS_PER_PAGE * (this.getCurrentPage(group) + 1));
    }

    public addToGroups(groupKeys: Array<GroupKey>, item: T) {
        for (const groupKey of groupKeys) {
            this.groupDisplay[groupKey.value] = groupKey.display;
            this.payload[groupKey.value] = groupKey.payload;

            if (!this.entries[groupKey.value]) this.entries[groupKey.value] = [];
            this.entries[groupKey.value].push(item);
        }
    }

    public insertAfterEntry(groupKeys: Array<GroupKey>, item: T, itemToInsert: T) {
        for (const groupKey of groupKeys) {
            const index = this.entries[groupKey.value].indexOf(item);
            this.entries[groupKey.value].splice(index + 1, 0, itemToInsert);
            this.groupDisplay[groupKey.value] = groupKey.display;
            this.payload[groupKey.value] = groupKey.payload;
        }
    }

    public remove(groupKeys: Array<GroupKey>, item: T) {
        const emptyGroupsToRemove: Array<string> = [];

        for (const key in this.entries) {
            const index = this.entries[key].indexOf(item);
            if (index >= 0) this.entries[key].splice(index, 1);
            if (this.entries[key].length === 0) emptyGroupsToRemove.push(key);
        }

        for (const emptyGroup of emptyGroupsToRemove) {
            delete this.entries[emptyGroup];
            delete this.groupDisplay[emptyGroup];
            let index = -1;
            for (let i = 0; i < this.keys.length; i++) {
                if (this.keys[i].value === emptyGroup) {
                    index = i;
                    break;
                }
            }
            if (index >= 0) this.keys.splice(index, 1);
        }
    }

    public sort(keyComparator: GroupKeyComparator, itemComparator: UserComparator) {
        this.keys = [];
        for (const key of Object.keys(this.entries)) {
            this.keys.push(new GroupKey(key, this.groupDisplay[key], this.payload[key]));
        }
        this.keys.sort(keyComparator);
        for (const key of this.keys) {
            this.entries[key.value].sort(itemComparator);
        }
    }
}

export class UserGroupingDefinition {
    constructor(
        public valueConvertor?: GroupUserValueConvertor,
        public groupKeyComparator?: GroupKeyComparatorFunc,
        public userComparator?: UserComparatorFunc
    ) {}
}
export type UserComparator = (a: UserStaffInfo, b: UserStaffInfo) => number;
export type UserComparatorFunc = (sortOrder: string) => UserComparator;

export class UserComparatorHelpers {
    public static Generic(pFieldName: string): UserComparatorFunc {
        return (sortOrder: string) => {
            const fieldName = pFieldName;
            return (a: UserStaffInfo, b: UserStaffInfo) => {
                const aValue = a.get(fieldName);
                const bValue = b.get(fieldName);
                if (bValue && !aValue) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                if (!bValue && aValue) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (!bValue && !aValue) {
                    return 0;
                }
                if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
                if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
                return 0;
            };
        };
    }

    public static String(pFieldName: string): UserComparatorFunc {
        return (sortOrder: string) => {
            const fieldName = pFieldName;
            return (a: UserStaffInfo, b: UserStaffInfo) => {
                let aValue: string = a.getAs<string>(fieldName);
                let bValue: string = b.getAs<string>(fieldName);

                if (bValue && !aValue) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                if (!bValue && aValue) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (!bValue && !aValue) {
                    return 0;
                }
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
                if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
                if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
                return 0;
            };
        };
    }

    public static Role(): UserComparatorFunc {
        return (sortOrder: string) => (a: UserStaffInfo, b: UserStaffInfo) => {
            let aRoleDescription: string = null;
            let bRoleDescription: string = null;

            if (a.Supervisor) {
                aRoleDescription = GlobalRoles.SUPERVISOR_ITEM.Description;
            } else {
                const securityRole = ResultGroupStaticServices.globalTablesService.getSecurityRole(a.GlobalRole);
                if (securityRole) aRoleDescription = securityRole.Description;
            }

            if (b.Supervisor) {
                bRoleDescription = GlobalRoles.SUPERVISOR_ITEM.Description;
            } else {
                const securityRole = ResultGroupStaticServices.globalTablesService.getSecurityRole(b.GlobalRole);
                if (securityRole) bRoleDescription = securityRole.Description;
            }

            if (bRoleDescription && !aRoleDescription) {
                return sortOrder === 'asc' ? 1 : -1;
            }
            if (!bRoleDescription && aRoleDescription) {
                return sortOrder === 'asc' ? -1 : 1;
            }
            if (!bRoleDescription && !aRoleDescription) {
                return 0;
            }
            aRoleDescription = aRoleDescription.toLowerCase();
            bRoleDescription = bRoleDescription.toLowerCase();
            if (aRoleDescription > bRoleDescription) return sortOrder === 'asc' ? 1 : -1;
            if (aRoleDescription < bRoleDescription) return sortOrder === 'asc' ? -1 : 1;
            return 0;
        };
    }
}
